import { useCallback, useEffect, useState } from 'react';
import { fetchApplications, submitApplication } from './api';

/* ==================================================================
   useJobApplication
   ------------------------------------------------------------------
   Everything the "Apply" button on a single job needs: whether this
   candidate has already applied, the in-flight state, and what to
   say when the server refuses.

   It exists so applying works the same on the public job page as it
   does inside the dashboard. The job page used to navigate the
   candidate to /student/dashboard instead of applying, which dropped
   the job they were looking at and created nothing.
   ================================================================== */

/**
 * What the UI should say and offer for each way an apply can fail.
 * `to` is a route inside the student portal - never off it.
 */
const NOTICE_BY_CODE = {
  PROFILE_NOT_VERIFIED: {
    tone: 'warning',
    title: 'Your profile needs to be verified first',
    action: 'Complete your profile',
    to: '/student/dashboard?tab=profile',
  },
  RESUME_MISSING: {
    tone: 'error',
    title: 'Upload your resume to apply',
    action: 'Upload resume',
    to: '/student/dashboard?tab=profile&resume=1',
  },
  RESUME_INACCESSIBLE: {
    tone: 'error',
    title: 'Application not submitted',
    action: 'Re-upload resume',
    to: '/student/dashboard?tab=profile&resume=1',
  },
  JOB_UNAVAILABLE: {
    tone: 'error',
    title: 'This role has closed',
    action: 'Browse open roles',
    to: '/student/dashboard?tab=opportunities',
  },
  APPLY_FAILED: {
    tone: 'error',
    title: 'Application not submitted',
  },
};

/**
 * @param {string|null} jobId   the job being viewed
 * @param {object|null} session signed-in user snapshot (localStorage "user")
 * @param {string|null} token   session token
 */
export default function useJobApplication(jobId, session, token) {
  const isStudent = !!token && session?.role === 'student';

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(isStudent);
  const [applying, setApplying] = useState(false);
  // Set only by an apply the candidate just made, so the page can celebrate a
  // fresh submission without also celebrating one from last week.
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [notice, setNotice] = useState(null);

  // Has this candidate already applied? Asked once per job, on the same route
  // the Submitted Applications list uses, so the two can never disagree.
  useEffect(() => {
    let cancelled = false;
    if (!isStudent || !jobId) { setLoading(false); return undefined; }
    setLoading(true);
    fetchApplications(token, { jobId })
      .then((apps) => { if (!cancelled) setApplication(apps[0] || null); })
      .catch(() => { /* leave it unknown; Apply still works and the server decides */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isStudent, jobId, token]);

  const apply = useCallback(async () => {
    if (!isStudent || !jobId || applying) return null;
    setApplying(true);
    setNotice(null);
    const result = await submitApplication(token, jobId);
    setApplying(false);

    if (result.ok) {
      setApplication(result.application);
      setJustSubmitted(true);
      return result;
    }

    // Already applied is not an error to show - it is the application itself.
    if (result.code === 'ALREADY_APPLIED' && result.application) {
      setApplication(result.application);
      return result;
    }

    const template = NOTICE_BY_CODE[result.code] || NOTICE_BY_CODE.APPLY_FAILED;
    setNotice({ ...template, message: result.message, code: result.code });
    return result;
  }, [applying, isStudent, jobId, token]);

  return {
    application,
    hasApplied: !!application,
    justSubmitted,
    loading,
    applying,
    notice,
    dismissNotice: () => setNotice(null),
    apply,
  };
}
